import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';

const { FiUsers, FiPlus, FiStar, FiCoffee, FiZap, FiTrendingUp, FiUser, FiCheck, FiLock } = FiIcons;

function AutoBaristaManager() {
  const { state, dispatch } = useGame();
  const [selectedBarista, setSelectedBarista] = useState(null);
  const [showHireModal, setShowHireModal] = useState(false);

  // Available barista types with different specialties
  const baristaTypes = [
    {
      id: 'trainee',
      name: 'Trainee Barista',
      description: 'Basic coffee drinks, learning the ropes',
      baseCost: 200,
      efficiency: 0.4, // 40% of player efficiency
      specialties: ['espresso', 'americano'],
      maxLevel: 3,
      avatar: '👨‍🎓',
      color: 'from-gray-400 to-gray-600'
    },
    {
      id: 'experienced',
      name: 'Experienced Barista',
      description: 'Skilled with milk drinks and consistency',
      baseCost: 500,
      efficiency: 0.6,
      specialties: ['espresso', 'americano', 'latte', 'cappuccino'],
      maxLevel: 5,
      avatar: '👩‍💼',
      color: 'from-blue-400 to-blue-600',
      requirement: 'Level 3'
    },
    {
      id: 'master',
      name: 'Master Barista',
      description: 'Expert in all coffee drinks including specialty',
      baseCost: 1000,
      efficiency: 0.8,
      specialties: ['espresso', 'americano', 'latte', 'cappuccino', 'macchiato', 'mocha'],
      maxLevel: 7,
      avatar: '👨‍🍳',
      color: 'from-purple-500 to-purple-700',
      requirement: 'Level 5'
    },
    {
      id: 'matcha_specialist',
      name: 'Matcha Specialist',
      description: 'Traditional Japanese tea ceremony expert',
      baseCost: 800,
      efficiency: 0.7,
      specialties: ['matcha_tea', 'matcha_latte', 'matcha_frappuccino'],
      maxLevel: 5,
      avatar: '🍃',
      color: 'from-green-500 to-green-700',
      requirement: 'Level 4'
    },
    {
      id: 'speed_demon',
      name: 'Speed Demon',
      description: 'Fast service, handles rush orders',
      baseCost: 750,
      efficiency: 0.9, // Very fast but limited drinks
      specialties: ['espresso', 'americano'],
      maxLevel: 4,
      avatar: '⚡',
      color: 'from-yellow-400 to-orange-500',
      requirement: 'Level 4'
    }
  ];

  // Calculate hiring cost based on current count
  const calculateHireCost = (baristaType, currentCount) => {
    return Math.floor(baristaType.baseCost * Math.pow(1.5, currentCount));
  };

  // Check if player can afford and meets requirements
  const canHire = (baristaType) => {
    const currentCount = state.autoBaristas?.[baristaType.id]?.count || 0;
    const cost = calculateHireCost(baristaType, currentCount);
    const meetsRequirement = !baristaType.requirement || 
      state.level >= parseInt(baristaType.requirement.split(' ')[1]);
    
    return state.coins >= cost && meetsRequirement && currentCount < baristaType.maxLevel;
  };

  // Hire a new barista
  const hireBarista = (baristaType) => {
    const currentCount = state.autoBaristas?.[baristaType.id]?.count || 0;
    const cost = calculateHireCost(baristaType, currentCount);
    
    if (!canHire(baristaType)) return;

    dispatch({ type: 'SPEND_COINS', payload: cost });
    dispatch({ 
      type: 'HIRE_BARISTA', 
      payload: { 
        baristaId: baristaType.id, 
        count: currentCount + 1,
        efficiency: baristaType.efficiency,
        specialties: baristaType.specialties
      } 
    });

    setShowHireModal(false);
  };

  // Calculate total production from all baristas
  const getTotalProduction = () => {
    if (!state.autoBaristas) return { coinsPerSecond: 0, customersPerMinute: 0 };

    let totalEfficiency = 0;
    let totalCustomers = 0;

    Object.entries(state.autoBaristas).forEach(([baristaId, barista]) => {
      const baristaType = baristaTypes.find(bt => bt.id === baristaId);
      if (baristaType && barista.count > 0) {
        const baseProduction = barista.count * baristaType.efficiency;
        totalEfficiency += baseProduction;
        totalCustomers += barista.count;
      }
    });

    // Apply cafe upgrade multipliers
    const machineBonus = (state.cafeUpgrades.espressoMachine || 1) * 0.1;
    const decorationBonus = (state.cafeUpgrades.decoration || 1) * 0.05;

    const coinsPerSecond = totalEfficiency * (1 + machineBonus + decorationBonus) * 0.8;
    const customersPerMinute = Math.max(1, totalCustomers * 2);

    return { coinsPerSecond, customersPerMinute };
  };

  const production = getTotalProduction();
  const totalBaristas = Object.values(state.autoBaristas || {}).reduce((sum, barista) => sum + barista.count, 0);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <SafeIcon icon={FiUsers} className="text-purple-500 mr-2 text-lg" />
          <div>
            <h3 className="font-bold text-gray-800">Auto Barista Team</h3>
            <p className="text-xs text-gray-500">Hire AI workers to automate your cafe</p>
          </div>
        </div>
        <motion.button
          className="bg-gradient-to-r from-green-500 to-green-600 text-white p-2 rounded-xl shadow-md"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowHireModal(true)}
        >
          <SafeIcon icon={FiPlus} className="text-lg" />
        </motion.button>
      </div>

      {/* Current Team Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-purple-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-purple-600">{totalBaristas}</div>
          <div className="text-xs text-purple-700">Baristas</div>
        </div>
        <div className="bg-green-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-green-600">
            ${production.coinsPerSecond.toFixed(1)}/s
          </div>
          <div className="text-xs text-green-700">Income</div>
        </div>
        <div className="bg-blue-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-blue-600">{production.customersPerMinute}</div>
          <div className="text-xs text-blue-700">Customers/min</div>
        </div>
      </div>

      {/* Current Baristas */}
      {totalBaristas > 0 ? (
        <div className="space-y-2 mb-4">
          <h4 className="text-sm font-bold text-gray-700">Active Team:</h4>
          {baristaTypes.map(baristaType => {
            const barista = state.autoBaristas?.[baristaType.id];
            if (!barista || barista.count === 0) return null;

            return (
              <motion.div
                key={baristaType.id}
                className={`bg-gradient-to-r ${baristaType.color} p-3 rounded-xl text-white`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{baristaType.avatar}</span>
                    <div>
                      <div className="font-bold text-sm">{baristaType.name}</div>
                      <div className="text-xs opacity-90">
                        {barista.count} worker{barista.count > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">
                      +${(barista.count * baristaType.efficiency * 0.8).toFixed(1)}/s
                    </div>
                    <div className="text-xs opacity-75">
                      {Math.round(baristaType.efficiency * 100)}% efficiency
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-400">
          <SafeIcon icon={FiUser} className="text-3xl mx-auto mb-2" />
          <p className="text-sm">No baristas hired yet</p>
          <p className="text-xs">Tap + to hire your first worker</p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-3">
        <h4 className="font-bold text-gray-800 text-sm mb-2">Team Performance:</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center">
            <SafeIcon icon={FiCoffee} className="text-amber-500 mr-1" />
            <span>Coffee Specialists: {Object.entries(state.autoBaristas || {}).filter(([id, barista]) => 
              ['trainee', 'experienced', 'master', 'speed_demon'].includes(id) && barista.count > 0
            ).reduce((sum, [, barista]) => sum + barista.count, 0)}</span>
          </div>
          <div className="flex items-center">
            <SafeIcon icon={FiStar} className="text-green-500 mr-1" />
            <span>Matcha Experts: {(state.autoBaristas?.matcha_specialist?.count || 0)}</span>
          </div>
        </div>
      </div>

      {/* Hire Modal */}
      <AnimatePresence>
        {showHireModal && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-4 text-white rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">Hire Auto Barista</h3>
                  <motion.button
                    className="bg-white/20 p-1 rounded-full"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowHireModal(false)}
                  >
                    <SafeIcon icon={FiUsers} className="text-lg" />
                  </motion.button>
                </div>
                <p className="text-white/80 text-sm mt-1">
                  Choose from skilled AI workers to automate your cafe
                </p>
              </div>

              {/* Barista Options */}
              <div className="p-4 space-y-3">
                {baristaTypes.map((baristaType, index) => {
                  const currentCount = state.autoBaristas?.[baristaType.id]?.count || 0;
                  const cost = calculateHireCost(baristaType, currentCount);
                  const canAfford = canHire(baristaType);
                  const meetsReq = !baristaType.requirement || 
                    state.level >= parseInt(baristaType.requirement.split(' ')[1]);
                  const isMaxed = currentCount >= baristaType.maxLevel;

                  return (
                    <motion.div
                      key={baristaType.id}
                      className={`border-2 rounded-xl p-4 transition-all ${
                        isMaxed ? 'border-gray-300 bg-gray-50' :
                        canAfford ? 'border-green-300 bg-green-50 hover:bg-green-100' :
                        !meetsReq ? 'border-red-300 bg-red-50' :
                        'border-gray-200 bg-gray-50'
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {/* Barista Info */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${baristaType.color} flex items-center justify-center text-2xl mr-3`}>
                            {baristaType.avatar}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-800">{baristaType.name}</h4>
                            <p className="text-sm text-gray-600">{baristaType.description}</p>
                            {baristaType.requirement && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                meetsReq ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                Requires {baristaType.requirement}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                        <div className="bg-white p-2 rounded">
                          <div className="text-gray-500 text-xs">Efficiency</div>
                          <div className="font-bold text-purple-600">
                            {Math.round(baristaType.efficiency * 100)}%
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <div className="text-gray-500 text-xs">Income/s</div>
                          <div className="font-bold text-green-600">
                            +${(baristaType.efficiency * 0.8).toFixed(1)}
                          </div>
                        </div>
                      </div>

                      {/* Specialties */}
                      <div className="mb-3">
                        <div className="text-xs text-gray-500 mb-1">Specialties:</div>
                        <div className="flex flex-wrap gap-1">
                          {baristaType.specialties.map(specialty => (
                            <span 
                              key={specialty}
                              className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs"
                            >
                              {specialty.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Current Count & Cost */}
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <span className="text-gray-500">Owned: </span>
                          <span className="font-bold">{currentCount}/{baristaType.maxLevel}</span>
                        </div>
                        <div className="text-right">
                          {isMaxed ? (
                            <div className="bg-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-bold">
                              MAX LEVEL
                            </div>
                          ) : (
                            <>
                              <div className="text-lg font-bold text-green-600">${cost}</div>
                              <motion.button
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                                  canAfford 
                                    ? 'bg-green-500 text-white hover:bg-green-600' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                                whileHover={canAfford ? { scale: 1.05 } : {}}
                                whileTap={canAfford ? { scale: 0.95 } : {}}
                                onClick={() => canAfford && hireBarista(baristaType)}
                                disabled={!canAfford}
                              >
                                {!meetsReq ? (
                                  <>
                                    <SafeIcon icon={FiLock} className="inline mr-1" />
                                    Locked
                                  </>
                                ) : !canAfford ? (
                                  'Not enough coins'
                                ) : (
                                  <>
                                    <SafeIcon icon={FiPlus} className="inline mr-1" />
                                    Hire
                                  </>
                                )}
                              </motion.button>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="p-4 bg-gray-50 rounded-b-3xl">
                <div className="text-center text-sm text-gray-600">
                  💡 Baristas work automatically when you're away
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AutoBaristaManager;